import { useState } from 'react';
import { Activity } from 'lucide-react';
import { exchangeDomain } from '../../../config/constants/exchangeDomains';
import { cn } from '@/lib/utils';

/**
 * The exchange's own mark, on the operator's domain.
 *
 * Falls back to the tinted activity dot this replaced — an exchange with no
 * recognisable operator site, or a logo the CDN does not carry, keeps a filled
 * circle rather than a hole in the row. NYSE keeps its inverted treatment so
 * the fallback still reads as a listing venue and not as a loading state.
 *
 * Same logo.dev source as StockLogo, so exchange and company marks resolve
 * through one service instead of two.
 */
const LOGO_DEV_KEY = 'pk_NKp9yy1hRFS87RNX42JkKA';

export function ExchangeLogo({
  mic,
  domain: operatorDomain,
  className,
}: {
  mic: string;
  domain?: string;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const domain = exchangeDomain(mic) ?? operatorDomain;
  const isNyse = mic === 'XNYS' || mic === 'NYSE';

  if (!domain || failed) {
    return (
      <span
        className={cn(
          'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
          isNyse ? 'bg-black' : 'bg-[#0088ff]',
          className,
        )}
      >
        <Activity
          strokeWidth={3}
          className={cn('h-3 w-3', isNyse ? 'text-[#0088ff]' : 'text-white')}
        />
      </span>
    );
  }

  return (
    <img
      // `size=128` for a 24px box: exchange marks are often wordmarks cropped
      // to a square, so they need the headroom to stay legible when scaled.
      src={`https://img.logo.dev/${domain}?token=${LOGO_DEV_KEY}&size=128`}
      alt={mic}
      title={mic}
      className={cn(
        'h-6 w-6 shrink-0 rounded-full border border-border/60 bg-white object-contain',
        className,
      )}
      onError={() => setFailed(true)}
    />
  );
}
